/**
 * localStorage persistence layer for user-defined custom creatures.
 *
 * Keys:
 *   frostgrave:custom-creatures   → Creature[]  (full hydrated objects, not ID references)
 */

import { Creature } from '../types';

const KEY_CUSTOM_CREATURES = 'frostgrave:custom-creatures';

function readAll(): Creature[] {
  try {
    const raw = localStorage.getItem(KEY_CUSTOM_CREATURES);
    return raw ? (JSON.parse(raw) as Creature[]) : [];
  } catch {
    return [];
  }
}

function writeAll(creatures: Creature[]): void {
  localStorage.setItem(KEY_CUSTOM_CREATURES, JSON.stringify(creatures));
}

export const customCreatureService = {
  getAll(): Creature[] {
    return readAll();
  },

  create(data: Omit<Creature, 'id'>): Creature {
    const creature: Creature = { ...data, id: crypto.randomUUID() };
    const all = readAll();
    writeAll([...all, creature]);
    return creature;
  },

  update(id: string, data: Partial<Omit<Creature, 'id'>>): Creature | null {
    const all = readAll();
    const idx = all.findIndex((c) => c.id === id);
    if (idx === -1) return null;
    const updated: Creature = { ...all[idx], ...data, id };
    const next = [...all];
    next[idx] = updated;
    writeAll(next);
    return updated;
  },

  delete(id: string): void {
    writeAll(readAll().filter((c) => c.id !== id));
  },

  /** Overwrites the entire local bestiary — used when applying a Drive download. */
  replaceAll(creatures: Creature[]): void {
    writeAll(creatures);
  },
};
